/**
 * CMS 权限资源编码与动作编码定义
 *
 * 遵循 YeeAuth 资源规范：
 * - 页面/菜单资源编码采用 kebab-case（如 'cms-content-asset'、'cms-site-website'）
 * - 保留驼峰别名（如 'cmsContent'、'cmsSite'）以保证后端 BFF Guard 校验完全兼容
 * - 页面动作编码采用 camelCase（如 'create'、'edit'、'publish'）
 */

export const PERMISSION_RESOURCE_CODES = {
  // 基础系统与权限管理
  permission: 'permission',

  // 顶部一级/模块级资源（保持历史驼峰兼容易于后端守卫）
  cmsWorkbench: 'cmsWorkbench',
  cmsAi: 'cmsAi',
  cmsContent: 'cmsContent',
  cmsSite: 'cmsSite',
  cmsSeo: 'cmsSeo',
  cmsProduct: 'cmsProduct',
  cmsMaterial: 'cmsMaterial',
  cmsSettings: 'cmsSettings',
  cmsChannel: 'cmsChannel',
  cmsOperation: 'cmsOperation',
  cmsLead: 'cmsLead',
  cmsAnalytics: 'cmsAnalytics',
  cmsIntegration: 'cmsIntegration',

  // 1. 工作台
  cmsWorkbenchOverview: 'cms-workbench-overview',

  // 2. AI 选题与创作
  cmsAiTopic: 'cms-ai-topic',
  cmsAiChannel: 'cms-ai-channel',

  // 3. 内容资产
  cmsContentAsset: 'cms-content-asset',
  cmsContentCategory: 'cms-content-category',
  cmsContentTag: 'cms-content-tag',
  cmsContentReview: 'cms-content-review',
  cmsContentMaterial: 'cms-content-material',
  cmsContentEditor: 'cms-content-editor',

  // 4. 站点运营
  cmsSiteList: 'cms-site-list',
  cmsSiteCategory: 'cms-site-category',
  cmsSiteAnalytics: 'cms-site-analytics',
  cmsSiteWebsite: 'cms-site-website',
  cmsSiteSitemap: 'cms-site-sitemap',
  cmsSiteRobots: 'cms-site-robots',

  // 5. SEO / GEO
  cmsSeoOverview: 'cms-seo-overview',
  cmsSeoQuestion: 'cms-seo-question',
  cmsSeoDiagnosis: 'cms-seo-diagnosis',
  cmsSeoAiSampling: 'cms-seo-ai-sampling',
  cmsSeoKeyword: 'cms-seo-keyword',
  cmsSeoDiagnosisDetail: 'cms-seo-diagnosis-detail',

  // 6. 品牌与产品
  cmsBusinessEnterprise: 'cms-business-enterprise',
  cmsBusinessBrand: 'cms-business-brand',
  cmsBusinessProduct: 'cms-business-product',
  cmsBusinessProductDetail: 'cms-business-product-detail',

  // 7. 设置
  cmsSettingsGeneral: 'cms-settings-general',
  cmsSettingsTheme: 'cms-settings-theme',
  cmsSettingsForms: 'cms-settings-forms',
  cmsSettingsFormCreate: 'cms-settings-form-create',
  cmsSettingsFormDetail: 'cms-settings-form-detail',
  cmsSettingsSubmissions: 'cms-settings-submissions',
  cmsSettingsSubmissionDetail: 'cms-settings-submission-detail',
} as const;

export type PermissionResourceCode =
  (typeof PERMISSION_RESOURCE_CODES)[keyof typeof PERMISSION_RESOURCE_CODES];

const standardActions = {
  view: 'view',
  create: 'create',
  edit: 'edit',
  delete: 'delete',
  submitReview: 'submitReview',
  review: 'review',
  publish: 'publish',
  withdraw: 'withdraw',
  export: 'export',
} as const;

export const PERMISSION_ACTION_CODES = {
  permission: {},

  // 模块级动作定义（兼容已有后端守卫）
  cmsWorkbench: {
    view: 'view',
    refresh: 'refresh',
    createContent: 'createContent',
  },
  cmsOperation: standardActions,
  cmsContent: {
    ...standardActions,
    batchPublish: 'batchPublish',
    batchArchive: 'batchArchive',
  },
  cmsAi: {
    view: 'view',
    create: 'create',
    acceptSuggestion: 'acceptSuggestion',
    generateTopic: 'generateTopic',
  },
  cmsSite: {
    ...standardActions,
    saveGeneral: 'saveGeneral',
    saveSeo: 'saveSeo',
    savePolicy: 'savePolicy',
  },
  cmsChannel: standardActions,
  cmsSeo: {
    view: 'view',
    create: 'create',
    edit: 'edit',
    delete: 'delete',
    diagnose: 'diagnose',
    createTask: 'createTask',
    manageTask: 'manageTask',
    export: 'export',
    import: 'import',
  },
  cmsMaterial: {
    ...standardActions,
    upload: 'upload',
    download: 'download',
    preview: 'preview',
  },
  cmsLead: {
    view: 'view',
    assign: 'assign',
    viewSensitive: 'viewSensitive',
    convertCrm: 'convertCrm',
    export: 'export',
  },
  cmsAnalytics: { view: 'view', export: 'export' },
  cmsProduct: standardActions,
  cmsSettings: {
    view: 'view',
    edit: 'edit',
    save: 'save',
    create: 'create',
    delete: 'delete',
    export: 'export',
  },
  cmsIntegration: {
    view: 'view',
    manageKey: 'manageKey',
    manageWebhook: 'manageWebhook',
    viewLog: 'viewLog',
  },

  // 1. 工作台
  cmsWorkbenchOverview: {
    view: 'view',
    refresh: 'refresh',
    createContent: 'createContent',
    handleReview: 'handleReview',
    viewAsset: 'viewAsset',
    quickAction: 'quickAction',
    viewSeoDetail: 'viewSeoDetail',
  },

  // 2. AI 选题与创作
  cmsAiTopic: {
    view: 'view',
    refresh: 'refresh',
    generateTopic: 'generateTopic',
    reanalyze: 'reanalyze',
    accept: 'accept',
    viewDetail: 'viewDetail',
    edit: 'edit',
    delete: 'delete',
    export: 'export',
    createDraft: 'createDraft',
    adaptChannel: 'adaptChannel',
  },
  cmsAiChannel: {
    view: 'view',
    regenerate: 'regenerate',
    saveVersion: 'saveVersion',
    downloadPackage: 'downloadPackage',
    copyContent: 'copyContent',
    createPublishTask: 'createPublishTask',
  },

  // 3. 内容资产
  cmsContentAsset: {
    view: 'view',
    create: 'create',
    edit: 'edit',
    submitReview: 'submitReview',
    withdraw: 'withdraw',
    publish: 'publish',
    delete: 'delete',
    batchPublish: 'batchPublish',
    batchSubmitReview: 'batchSubmitReview',
    batchArchive: 'batchArchive',
    preview: 'preview',
  },
  cmsContentCategory: {
    view: 'view',
    create: 'create',
    edit: 'edit',
    delete: 'delete',
  },
  cmsContentTag: {
    view: 'view',
    create: 'create',
    edit: 'edit',
    delete: 'delete',
    batchDelete: 'batchDelete',
  },
  cmsContentReview: {
    view: 'view',
    approve: 'approve',
    reject: 'reject',
    batchApprove: 'batchApprove',
    batchReject: 'batchReject',
    diff: 'diff',
  },
  cmsContentMaterial: {
    view: 'view',
    upload: 'upload',
    preview: 'preview',
    download: 'download',
    delete: 'delete',
  },
  cmsContentEditor: {
    view: 'view',
    saveDraft: 'saveDraft',
    submitReview: 'submitReview',
    publish: 'publish',
    preview: 'preview',
  },

  // 4. 站点运营
  cmsSiteList: {
    view: 'view',
    create: 'create',
    edit: 'edit',
    delete: 'delete',
    switchSite: 'switchSite',
    settings: 'settings',
  },
  cmsSiteCategory: {
    view: 'view',
    create: 'create',
    edit: 'edit',
    delete: 'delete',
  },
  cmsSiteAnalytics: {
    view: 'view',
    refresh: 'refresh',
    export: 'export',
  },
  cmsSiteWebsite: {
    view: 'view',
    saveGeneral: 'saveGeneral',
    saveSeo: 'saveSeo',
    editPolicy: 'editPolicy',
    previewPolicy: 'previewPolicy',
    savePolicy: 'savePolicy',
  },
  cmsSiteSitemap: {
    view: 'view',
    generate: 'generate',
    save: 'save',
  },
  cmsSiteRobots: {
    view: 'view',
    save: 'save',
    reset: 'reset',
  },

  // 5. SEO / GEO
  cmsSeoOverview: {
    view: 'view',
    refresh: 'refresh',
    export: 'export',
    createTask: 'createTask',
  },
  cmsSeoQuestion: {
    view: 'view',
    create: 'create',
    import: 'import',
    export: 'export',
    edit: 'edit',
    delete: 'delete',
    generateAnswer: 'generateAnswer',
  },
  cmsSeoDiagnosis: {
    view: 'view',
    diagnose: 'diagnose',
    createTask: 'createTask',
    updateTaskStatus: 'updateTaskStatus',
    viewReport: 'viewReport',
  },
  cmsSeoAiSampling: {
    view: 'view',
    sample: 'sample',
    configSampling: 'configSampling',
    export: 'export',
  },
  cmsSeoKeyword: {
    view: 'view',
    create: 'create',
    import: 'import',
    export: 'export',
    edit: 'edit',
    delete: 'delete',
    expandKeywords: 'expandKeywords',
  },
  cmsSeoDiagnosisDetail: {
    view: 'view',
    rediagnose: 'rediagnose',
    autoOptimize: 'autoOptimize',
    createTask: 'createTask',
  },

  // 6. 品牌与产品
  cmsBusinessEnterprise: {
    view: 'view',
    save: 'save',
    uploadAsset: 'uploadAsset',
  },
  cmsBusinessBrand: {
    view: 'view',
    create: 'create',
    edit: 'edit',
    delete: 'delete',
    switch: 'switch',
  },
  cmsBusinessProduct: {
    view: 'view',
    create: 'create',
    edit: 'edit',
    delete: 'delete',
    export: 'export',
    viewOverview: 'viewOverview',
  },
  cmsBusinessProductDetail: {
    view: 'view',
    edit: 'edit',
    linkContent: 'linkContent',
    uploadEvidence: 'uploadEvidence',
  },

  // 7. 设置
  cmsSettingsGeneral: {
    view: 'view',
    save: 'save',
  },
  cmsSettingsTheme: {
    view: 'view',
    save: 'save',
    reset: 'reset',
  },
  cmsSettingsForms: {
    view: 'view',
    create: 'create',
    edit: 'edit',
    delete: 'delete',
    copy: 'copy',
    viewSubmissions: 'viewSubmissions',
  },
  cmsSettingsFormCreate: {
    view: 'view',
    save: 'save',
    publish: 'publish',
    preview: 'preview',
  },
  cmsSettingsFormDetail: {
    view: 'view',
    save: 'save',
    publish: 'publish',
    preview: 'preview',
  },
  cmsSettingsSubmissions: {
    view: 'view',
    export: 'export',
    process: 'process',
    delete: 'delete',
  },
  cmsSettingsSubmissionDetail: {
    view: 'view',
    markProcessed: 'markProcessed',
    export: 'export',
    delete: 'delete',
  },
} as const;
